import { media, visibility } from '~/styles/mixins';
import { Translation, useCurrentLanguage } from '~/components/Translation';
import Button from '~/components/Button';
import LinkBody from '~/components/LinkBody';
import {
	BenefitCard,
	CTARow,
	FaqBlock,
	Header,
	ImageBlock,
	Image640x560,
	MultipleSrcPicture,
	TestimonialCarousel,
} from '~/components/Marketing';
import ResponsiveText from '~/components/ResponsiveText';
import React, { Fragment, useCallback } from 'react';
import styled from 'styled-components';
import Text from '~/components/Text';
import analytics from '~/services/analytics';
import googleTagManager from '~/services/googleTagManager';
import usePageTrack from '~/services/analytics/usePageTrack';

import People from '~/components/CustomIcons/Marketing/People';
import Camera from '~/components/CustomIcons/Marketing/Camera';
import LiveStreaming from '~/components/CustomIcons/Marketing/LiveStreaming';
import { HStack, VStack } from '~/components/ViewStacks';
import AnimatedLogoList from '~/templates/LandingV2/components/AnimatedLogoList';

import HeroSmallImageWebp from './hero_image_small.webp';

import HeroSmallImagePng from './hero_image_small.png';
import HeroLargeImageWebp1x from './hero_image_1x.webp';
import HeroLargeImageWebp2x from './hero_image_2x.webp';
import HeroLargeImagePng from './hero_image.png';
import BlockImageTwoWebp1x from './block_image_two_1x.webp';
import BlockImageTwoWebp2x from './block_image_two_2x.webp';
import BlockImageTwoPng from './block_image_two.png';
import BlockImageThreeWebp1x from './block_image_three_1x.webp';
import BlockImageThreeWebp2x from './block_image_three_2x.webp';
import BlockImageThreePng from './block_image_three.png';
import BlockImageOneWebp1x from './block_image_one_1x.webp';
import BlockImageOneWebp2x from './block_image_one_2x.webp';
import BlockImageOnePng from './block_image_one.png';

import testimonial1AuthorPng from './testimonial_1_author.png';
import testimonial1AuthorWebp from './testimonial_1_author.webp';
import testimonial2AuthorPng from './testimonial_2_author.png';
import testimonial2AuthorWebp from './testimonial_2_author.webp';
import testimonial3AuthorPng from './testimonial_3_author.png';
import testimonial3AuthorWebp from './testimonial_3_author.webp';

import ListItem from './components/ListItem';

const StyledHeader = styled(Header)`
	/* This is not a good practice but we hope that we'll clean this with the upcoming system design! */
	.headerSubtitle {
		max-width: unset;
	}
`;

const HeroLarge = styled.div`
	width: 100%;
	margin: 0 auto;
	${visibility.showLarge}
`;

const HeroLargeImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const HeroSmall = styled.div`
	width: 100%;
	margin: 0 auto;
	padding: 0 24px;
	${media.medium`
        padding: 0 80px;
    `}
	${visibility.hideLarge}
`;

const HeroSmallImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const BenefitBlockWrap = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 128px 0;
`;

const BenefitBlock = styled.div`
	padding: 32px 24px;
	display: grid;
	grid-gap: 32px;
	grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
	align-items: stretch;
	width: 100%;
	max-width: 1352px;
	margin: 0 auto;
	${media.belowLarge`
    flex-direction: column;
    align-items: center;
  `}
	${media.belowSmall`
		grid-template-columns: 1fr;
	`}
`;

const BlockHeaderText = styled(ResponsiveText)`
	margin-bottom: 16px;
	${media.xLarge`
    margin-bottom: 40px;
  `}
`;

const BenefitHeaderText = styled(BlockHeaderText)`
	text-align: center;
`;

const BlockSubHeaderText = styled(ResponsiveText)`
	margin-bottom: 8px;
`;

const BlockBodyText = styled(Text)`
	margin-bottom: 16px;
	${media.xLarge`
        margin-bottom: 14px;
  `}
`;

const StyledButton = styled(Button)`
	min-width: 196px;
	flex: 1;
	margin-left: 12px;
	max-width: 500px;
	min-height: 52px;
	${media.belowSmall`
		min-width: 142px;
    margin-left: 0;
  `}
`;

const StyledHStack = styled(HStack)`
	gap: 32px;
	${media.belowSmall`
		gap: 16px;
	`}
`;

const StyledTitle = styled(Text).attrs(props => ({
	variant: 'body',
	color: props.theme.colors.white,
}))`
	font-size: 16px;
	font-weight: 600;
	line-height: 20px;
`;

const StyledSubtitle = styled(Text).attrs(props => ({
	variant: 'body3',
	color: props.theme.colors.white,
}))`
	font-size: 12px;
	font-weight: 400;
	line-height: 15px;
`;
const AfterCtaText = styled(ResponsiveText)`
	padding: 0 24px;
	margin: 40px auto 0;
	text-align: center;
`;

const StyledCTARow = styled(CTARow).attrs(props => ({
	backgroundColor: props.theme.colors.background.dark,
}))``;

const faqList = [
	{
		question: <Translation tid="landingBusiness.faq.question.one" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingBusiness.faq.question.one.answer" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingBusiness.faq.question.two" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingBusiness.faq.question.two.answer" />
				</ResponsiveText>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.two.listItem.one" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.two.listItem.two" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.two.listItem.three" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.two.listItem.four" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.two.listItem.five" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.two.listItem.six" />
				</ListItem>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingBusiness.faq.question.three" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingBusiness.faq.question.three.answer" />
				</ResponsiveText>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.one" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.two" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.three" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.four" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.five" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.six" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.seven" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.eight" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.three.listItem.nine" />
				</ListItem>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingBusiness.faq.question.five" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingBusiness.faq.question.five.answer" />
				</ResponsiveText>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.one" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.two" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.four" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.five" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.six" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.seven" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.eight" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.nine" />
				</ListItem>
				<ListItem>
					<Translation tid="landingBusiness.faq.question.five.listItem.ten" />
				</ListItem>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingBusiness.faq.question.seven" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingBusiness.faq.question.seven.answer" />
				</ResponsiveText>
			</Fragment>
		),
	},
];

const StyledTestimonialsRevamp = styled(TestimonialCarousel).attrs(props => ({
	backgroundColor: props.theme.colors.white,
}))``;

const TestimonialsBlock = () => (
	<StyledTestimonialsRevamp
		header={<Translation tid="landingBusiness.testimonials.header" />}
		testimonials={[
			{
				authorImagePng: testimonial1AuthorPng,
				authorImageWebp: testimonial1AuthorWebp,
				citation: <Translation tid="landingBusiness.testimonials.1.name" />,
				quote: <Translation tid="landingBusiness.testimonials.1.content" />,
			},
			{
				authorImagePng: testimonial2AuthorPng,
				authorImageWebp: testimonial2AuthorWebp,
				citation: <Translation tid="landingBusiness.testimonials.2.name" />,
				quote: <Translation tid="landingBusiness.testimonials.2.content" />,
			},
			{
				authorImagePng: testimonial3AuthorPng,
				authorImageWebp: testimonial3AuthorWebp,
				citation: <Translation tid="landingBusiness.testimonials.3.name" />,
				quote: <Translation tid="landingBusiness.testimonials.3.content" />,
			},
		]}
	/>
);

const Enterprise = () => {
	const contactUs = useCallback(() => {
		analytics.track(analytics.events.BUSINESS_PAGE_REQUEST_DEMO_BUTTON_CLICKED);
		googleTagManager.event(
			analytics.events.BUSINESS_PAGE_REQUEST_DEMO_BUTTON_CLICKED,
		);
	}, []);

	const contact = useCallback(() => {
		analytics.track(analytics.events.BUSINESS_PAGE_NEED_HELP_CLICKED);
	}, []);

	const language = useCurrentLanguage();

	usePageTrack({
		name: analytics.events.BUSINESS,
		category: analytics.events.category.MARKETING,
	});

	return (
		<main role="main">
			<StyledHeader
				title={<Translation tid="landingBusiness.title" />}
				subtitles={[<Translation key="1" tid="landingBusiness.subtitle" />]}
				cta={
					<StyledHStack>
						<StyledButton size="large" onClick={contactUs} to="/contact-us">
							<StyledTitle>
								<Translation tid="landingBusiness.action" />
							</StyledTitle>
						</StyledButton>
						<StyledButton size="large" onClick={contact} to="/contact">
							<VStack alignment="center">
								<StyledTitle>
									<Translation tid="landingBusiness.cta.needHelp.title" />
								</StyledTitle>
								<StyledSubtitle>
									<Translation tid="landingBusiness.cta.needHelp.subtitle" />
								</StyledSubtitle>
							</VStack>
						</StyledButton>
					</StyledHStack>
				}
			/>
			<AfterCtaText variant="body">
				<Translation
					tid="landingBusiness.afterCta"
					components={{ link: <LinkBody href="/plan" /> }}
				/>
			</AfterCtaText>
			<HeroLarge>
				<HeroLargeImage
					fallbackSrc={HeroLargeImagePng}
					webpSrc={`${HeroLargeImageWebp1x}, ${HeroLargeImageWebp2x} 2x`}
					alt=""
				/>
			</HeroLarge>
			<HeroSmall>
				<HeroSmallImage
					fallbackSrc={HeroSmallImagePng}
					webpSrc={HeroSmallImageWebp}
					alt=""
				/>
			</HeroSmall>
			<AnimatedLogoList greyBackground />
			<BenefitBlockWrap>
				<BenefitHeaderText variant="h2">
					<Translation tid="landingBusiness.benefitCard.heading" />
				</BenefitHeaderText>
				<BenefitBlock>
					<BenefitCard
						headingText={
							<Translation tid="landingBusiness.benefitCard.one.header" />
						}
						bodyText={
							<Translation tid="landingBusiness.benefitCard.one.body" />
						}
						icon={<LiveStreaming size="48" />}
						titleTag="h2"
					/>
					<BenefitCard
						headingText={
							<Translation tid="landingBusiness.benefitCard.two.header" />
						}
						bodyText={
							<Translation tid="landingBusiness.benefitCard.two.body" />
						}
						icon={<People size="48" />}
						titleTag="h2"
					/>
					<BenefitCard
						headingText={
							<Translation tid="landingBusiness.benefitCard.three.header" />
						}
						bodyText={
							<Translation tid="landingBusiness.benefitCard.three.body" />
						}
						icon={<Camera size="48" />}
						titleTag="h2"
					/>
				</BenefitBlock>
			</BenefitBlockWrap>
			<ImageBlock
				image={
					<Image640x560
						fallbackSrc={BlockImageOnePng}
						webpSrc={`${BlockImageOneWebp1x}, ${BlockImageOneWebp2x} 2x`}
						alt=""
					/>
				}
				color="slate"
				reverse
			>
				<BlockHeaderText variant="h2">
					<Translation tid="landingBusiness.block.one.header" />
				</BlockHeaderText>
				<BlockSubHeaderText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.one.body.one.header" />
				</BlockSubHeaderText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.one.body.one.body" />
				</BlockBodyText>
				<BlockSubHeaderText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.one.body.two.header" />
				</BlockSubHeaderText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.one.body.two.body" />
				</BlockBodyText>
				{language === 'en' && (
					<>
						<BlockSubHeaderText variant="h6" component="h3">
							<Translation tid="landingBusiness.block.one.body.three.header" />
						</BlockSubHeaderText>
						<BlockBodyText variant="body">
							<Translation tid="landingBusiness.block.one.body.three.body.v2" />
						</BlockBodyText>
					</>
				)}
				<BlockSubHeaderText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.one.body.four.header" />
				</BlockSubHeaderText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.one.body.four.body" />
				</BlockBodyText>
			</ImageBlock>
			<ImageBlock
				image={
					<Image640x560
						fallbackSrc={BlockImageTwoPng}
						webpSrc={`${BlockImageTwoWebp1x}, ${BlockImageTwoWebp2x} 2x`}
						alt=""
					/>
				}
				color="white"
			>
				<BlockHeaderText variant="h2">
					<Translation tid="landingBusiness.block.two.header" />
				</BlockHeaderText>
				<BlockSubHeaderText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.two.body.one.header" />
				</BlockSubHeaderText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.two.body.one.body" />
				</BlockBodyText>
				<BlockSubHeaderText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.two.body.two.header" />
				</BlockSubHeaderText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.two.body.two.body" />
				</BlockBodyText>
				<BlockSubHeaderText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.two.body.three.header" />
				</BlockSubHeaderText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.two.body.three.body" />
				</BlockBodyText>
			</ImageBlock>
			<ImageBlock
				image={
					<Image640x560
						fallbackSrc={BlockImageThreePng}
						webpSrc={`${BlockImageThreeWebp1x}, ${BlockImageThreeWebp2x} 2x`}
						alt=""
					/>
				}
				color="slate"
				reverse
			>
				<BlockHeaderText variant="h2">
					<Translation tid="landingBusiness.block.three.header" />
				</BlockHeaderText>
				<ResponsiveText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.three.body.one.header" />
				</ResponsiveText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.three.body.one.body" />
				</BlockBodyText>
				<ResponsiveText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.three.body.two.header" />
				</ResponsiveText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.three.body.two.body" />
				</BlockBodyText>
				<ResponsiveText variant="h6" component="h3">
					<Translation tid="landingBusiness.block.three.body.three.header" />
				</ResponsiveText>
				<BlockBodyText variant="body">
					<Translation tid="landingBusiness.block.three.body.three.body" />
				</BlockBodyText>
			</ImageBlock>
			<TestimonialsBlock />
			<FaqBlock
				faqList={faqList}
				header={<Translation tid="landingBusiness.faq.header" />}
			/>
			<StyledCTARow
				title={<Translation tid="landingBusiness.cta.title" />}
				subtitle={<Translation tid="landingBusiness.cta.subtitle" />}
				cta={
					<StyledButton to="/contact-us">
						<Translation tid="landingBusiness.cta.action" />
					</StyledButton>
				}
			/>
		</main>
	);
};

export default Enterprise;
